import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { http } from "@/lib/api";
import { Card, PageTitle } from "@/components/portal/ui";

// Two-way messaging between a signed-in client and their consultant (general thread).
export default function Messages() {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const load = () => http.get("/portal/messages?product=general").then((r) => setMsgs(r.data)).catch(() => setMsgs([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  if (!msgs) return <div className="text-[#6B665E]">Loading…</div>;
  const unread = msgs.filter((m) => m.sender === "consultant" && !m.read).length;

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await http.post("/portal/messages", { product: "general", text }); setText(""); await load(); }
    catch (e) { /* surfaced via disabled state */ } finally { setSending(false); }
  };

  return (
    <div>
      <PageTitle eyebrow="Messages" title="Your Consultant Messages"
        sub="Send a message to Andrea Hurley and see her replies here. Please don't use messages for medical emergencies." />
      {unread > 0 && (
        <div className="mb-4 text-sm text-[#9F634E]" data-testid="messages-unread">
          {unread} new repl{unread === 1 ? "y" : "ies"} from your consultant
        </div>
      )}
      <Card className="p-0 overflow-hidden max-w-2xl">
        <div className="p-5 space-y-3 max-h-[55vh] overflow-y-auto" data-testid="messages-thread">
          {msgs.length === 0 ? (
            <p className="text-[#6B665E] text-sm text-center py-8">No messages yet. Start the conversation below — your consultant will reply here.</p>
          ) : msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.sender}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.sender === "customer" ? "bg-[#9F634E] text-white" : "bg-[#F3EFE9] text-[#1A1918]"}`}>
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                <div className={`text-[10px] mt-1 flex items-center gap-1.5 ${m.sender === "customer" ? "text-white/70" : "text-[#8A998C]"}`}>
                  <span>{m.sender === "customer" ? "You" : "Andrea Hurley"}</span>
                  <span>·</span>
                  <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                  {m.sender === "consultant" && !m.read && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#9F634E] inline-block" title="New" />}
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#E5E1D8] p-3 flex items-end gap-2 bg-[#FAF8F3]">
          <textarea data-testid="messages-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(); }}
            placeholder="Write a message to your consultant…"
            className="flex-1 px-3 py-2 rounded-lg border border-[#E5E1D8] bg-white text-sm text-[#1A1918] focus:border-[#9F634E] outline-none resize-none" />
          <button data-testid="messages-send" onClick={send} disabled={sending || !text.trim()}
            className="inline-flex items-center gap-1.5 bg-[#9F634E] text-white rounded-full px-4 py-2.5 text-sm font-medium hover:bg-[#8a5342] transition-colors disabled:opacity-50">
            <Send size={14} /> Send
          </button>
        </div>
      </Card>
    </div>
  );
}
